import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useTheme } from '../../contexts/ThemeContext';

const StatsDashboard = () => {
  const { isDarkMode } = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('7d');

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get('/monitoring/analytics/stats', {
        params: { period: selectedPeriod }
      });
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatDuration = (minutes) => {
    if (minutes < 1) {
      return '< 1 min';
    } else if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      return `${hours}h ${mins}min`;
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, twoLine }) => {
    return (
      <div
        style={{
          backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
          border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
          borderRadius: '8px',
          padding: '1.5rem',
          textAlign: 'center',
          boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
          flex: 1,
          minWidth: '200px'
        }}
      >
        <div style={{ marginBottom: '0.5rem', fontSize: '2rem' }}>
          {icon}
        </div>
        {twoLine ? (
          <div
            style={{
              fontSize: '0.85rem',
              color: isDarkMode ? '#aaa' : '#666',
              marginBottom: '0.5rem',
              textTransform: 'uppercase',
              fontWeight: '500',
              letterSpacing: '0.05em',
              lineHeight: '1.3'
            }}
          >
            {title.split(' ').map((word, index, array) => (
              <span key={index}>
                {word}
                {index === 0 && array.length > 1 && <br />}
              </span>
            ))}
          </div>
        ) : (
          <div
            style={{
              fontSize: '0.85rem',
              color: isDarkMode ? '#aaa' : '#666',
              marginBottom: '0.5rem',
              textTransform: 'uppercase',
              fontWeight: '500',
              letterSpacing: '0.05em',
              lineHeight: '1.3'
            }}
          >
            {title}
          </div>
        )}
      <div
        style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          color: color,
          marginBottom: subtitle ? '0.25rem' : '0'
        }}
      >
        {value}
      </div>
      {subtitle && (
        <div
          style={{
            fontSize: '0.75rem',
            color: isDarkMode ? '#888' : '#999',
            marginTop: '0.25rem'
          }}
        >
          {subtitle}
        </div>
      )}
      </div>
    );
  };

  const periods = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: 'all', label: 'All Time' }
  ];

  if (loading) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        color: isDarkMode ? '#aaa' : '#666'
      }}>
        Loading statistics...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        color: '#dc3545'
      }}>
        {error}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const maxDailyLogins = stats.dailyLogins && stats.dailyLogins.length > 0
    ? Math.max(...stats.dailyLogins.map(d => d.count))
    : 1;

  const maxMonthlyLogins = stats.monthlyLogins && stats.monthlyLogins.length > 0
    ? Math.max(...stats.monthlyLogins.map(m => m.count))
    : 1;

  const maxDailyConnections = stats.dailyConnections && stats.dailyConnections.length > 0
    ? Math.max(...stats.dailyConnections.map(d => d.count))
    : 1;

  const maxMonthlyConnections = stats.monthlyConnections && stats.monthlyConnections.length > 0
    ? Math.max(...stats.monthlyConnections.map(m => m.count))
    : 1;

  return (
    <div style={{ padding: '2rem 0' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{
          margin: 0,
          marginBottom: '1rem',
          color: isDarkMode ? 'white' : '#333'
        }}>
          User Statistics
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {periods.map(period => (
            <button
              key={period.value}
              onClick={() => setSelectedPeriod(period.value)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: selectedPeriod === period.value
                  ? (isDarkMode ? '#0099FF' : '#0099FF')
                  : (isDarkMode ? '#333' : '#e9ecef'),
                color: selectedPeriod === period.value ? 'white' : (isDarkMode ? '#ccc' : '#666'),
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: selectedPeriod === period.value ? '600' : '400',
                transition: 'all 0.2s ease'
              }}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Stats Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}
      >
        <StatCard
          title="TOTAL LOGINS"
          value={stats.totalLogins}
          icon="🔐"
          color="#0099FF"
          twoLine={true}
        />
        <StatCard
          title="MCP-PROXY CONNECTIONS"
          value={stats.mcpProxyConnections || 0}
          icon="🔌"
          color="#6c757d"
        />
        <StatCard
          title="Avg Session Duration"
          value={formatDuration(stats.avgSessionDurationMinutes)}
          icon="⏱️"
          color={isDarkMode ? 'white' : '#333'}
          subtitle={stats.avgSessionDurationMs > 0 ? `${Math.round(stats.avgSessionDurationMs / 1000)} seconds` : 'No data'}
        />
        <StatCard
          title="Avg Connection Duration"
          value={formatDuration(stats.avgMCPProxyConnectionDurationMinutes || 0)}
          icon="⏱️"
          color={isDarkMode ? 'white' : '#333'}
          subtitle={stats.avgMCPProxyConnectionDurationMs > 0 ? `${Math.round(stats.avgMCPProxyConnectionDurationMs / 1000)} seconds` : 'No data'}
        />
        <StatCard
          title="NEW USERS"
          value={stats.newUsers}
          icon="✨"
          color="#ffc107"
          twoLine={true}
        />
      </div>

      {/* Daily Logins Chart */}
      {stats.dailyLogins && stats.dailyLogins.length > 0 && (
        <div
          style={{
            backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
            borderRadius: '8px',
            padding: '2rem',
            marginBottom: '2rem'
          }}
        >
          <h3 style={{
            color: isDarkMode ? 'white' : '#333',
            marginBottom: '1.5rem',
            fontSize: '1.2rem'
          }}>
            Daily Logins
          </h3>
          <div style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '0.5rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '0.5rem',
              height: '200px',
              padding: '1rem 0',
              minWidth: 'min-content'
            }}>
            {stats.dailyLogins.map((day, index) => {
              const height = maxDailyLogins > 0 ? (day.count / maxDailyLogins) * 100 : 0;
              return (
                <div
                  key={index}
                  style={{
                    flex: '0 0 auto',
                    minWidth: '40px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: `${height}%`,
                      backgroundColor: '#0099FF',
                      borderRadius: '4px 4px 0 0',
                      minHeight: day.count > 0 ? '4px' : '0',
                      transition: 'height 0.3s ease',
                      position: 'relative'
                    }}
                    title={`${day.date}: ${day.count} logins`}
                  >
                    {day.count > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '-1.5rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '0.75rem',
                        color: '#0099FF',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {day.count}
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: '0.7rem',
                    color: isDarkMode ? '#888' : '#999',
                    transform: 'rotate(-45deg)',
                    transformOrigin: 'center',
                    whiteSpace: 'nowrap',
                    marginTop: '0.5rem'
                  }}>
                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      )}

      {/* Monthly Logins Chart - Only show for "All Time" */}
      {selectedPeriod === 'all' && stats.monthlyLogins && stats.monthlyLogins.length > 0 && (
        <div
          style={{
            backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
            borderRadius: '8px',
            padding: '2rem',
            marginBottom: '2rem'
          }}
        >
          <h3 style={{
            color: isDarkMode ? 'white' : '#333',
            marginBottom: '1.5rem',
            fontSize: '1.2rem'
          }}>
            Monthly Logins
          </h3>
          <div style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '0.5rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '0.5rem',
              height: '200px',
              padding: '1rem 0',
              minWidth: 'min-content'
            }}>
            {stats.monthlyLogins.map((month, index) => {
              const height = maxMonthlyLogins > 0 ? (month.count / maxMonthlyLogins) * 100 : 0;
              return (
                <div
                  key={index}
                  style={{
                    flex: '0 0 auto',
                    minWidth: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: `${height}%`,
                      backgroundColor: '#0099FF',
                      borderRadius: '4px 4px 0 0',
                      minHeight: month.count > 0 ? '4px' : '0',
                      transition: 'height 0.3s ease',
                      position: 'relative'
                    }}
                    title={`${month.date}: ${month.count} logins`}
                  >
                    {month.count > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '-1.5rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '0.75rem',
                        color: '#0099FF',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {month.count}
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: '0.7rem',
                    color: isDarkMode ? '#888' : '#999',
                    transform: 'rotate(-45deg)',
                    transformOrigin: 'center',
                    whiteSpace: 'nowrap',
                    marginTop: '0.5rem'
                  }}>
                    {new Date(month.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      )}

      {/* Daily Connections Chart */}
      {stats.dailyConnections && stats.dailyConnections.length > 0 && (
        <div
          style={{
            backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
            borderRadius: '8px',
            padding: '2rem',
            marginBottom: '2rem'
          }}
        >
          <h3 style={{
            color: isDarkMode ? 'white' : '#333',
            marginBottom: '1.5rem',
            fontSize: '1.2rem'
          }}>
            Daily Connections
          </h3>
          <div style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '0.5rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '0.5rem',
              height: '200px',
              padding: '1rem 0',
              minWidth: 'min-content'
            }}>
            {stats.dailyConnections.map((day, index) => {
              const height = maxDailyConnections > 0 ? (day.count / maxDailyConnections) * 100 : 0;
              return (
                <div
                  key={index}
                  style={{
                    flex: '0 0 auto',
                    minWidth: '40px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: `${height}%`,
                      backgroundColor: '#6c757d',
                      borderRadius: '4px 4px 0 0',
                      minHeight: day.count > 0 ? '4px' : '0',
                      transition: 'height 0.3s ease',
                      position: 'relative'
                    }}
                    title={`${day.date}: ${day.count} connections`}
                  >
                    {day.count > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '-1.5rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {day.count}
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: '0.7rem',
                    color: isDarkMode ? '#888' : '#999',
                    transform: 'rotate(-45deg)',
                    transformOrigin: 'center',
                    whiteSpace: 'nowrap',
                    marginTop: '0.5rem'
                  }}>
                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      )}

      {/* Monthly Connections Chart - Only show for "All Time" */}
      {selectedPeriod === 'all' && stats.monthlyConnections && stats.monthlyConnections.length > 0 && (
        <div
          style={{
            backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
            borderRadius: '8px',
            padding: '2rem',
            marginBottom: '2rem'
          }}
        >
          <h3 style={{
            color: isDarkMode ? 'white' : '#333',
            marginBottom: '1.5rem',
            fontSize: '1.2rem'
          }}>
            Monthly Connections
          </h3>
          <div style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '0.5rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '0.5rem',
              height: '200px',
              padding: '1rem 0',
              minWidth: 'min-content'
            }}>
            {stats.monthlyConnections.map((month, index) => {
              const height = maxMonthlyConnections > 0 ? (month.count / maxMonthlyConnections) * 100 : 0;
              return (
                <div
                  key={index}
                  style={{
                    flex: '0 0 auto',
                    minWidth: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: `${height}%`,
                      backgroundColor: '#6c757d',
                      borderRadius: '4px 4px 0 0',
                      minHeight: month.count > 0 ? '4px' : '0',
                      transition: 'height 0.3s ease',
                      position: 'relative'
                    }}
                    title={`${month.date}: ${month.count} connections`}
                  >
                    {month.count > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '-1.5rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {month.count}
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: '0.7rem',
                    color: isDarkMode ? '#888' : '#999',
                    transform: 'rotate(-45deg)',
                    transformOrigin: 'center',
                    whiteSpace: 'nowrap',
                    marginTop: '0.5rem'
                  }}>
                    {new Date(month.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      )}

      {/* Top Users */}
      {stats.topUsers && stats.topUsers.length > 0 && (
        <div
          style={{
            backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
            borderRadius: '8px',
            padding: '2rem'
          }}
        >
          <h3 style={{
            color: isDarkMode ? 'white' : '#333',
            marginBottom: '1.5rem',
            fontSize: '1.2rem'
          }}>
            Top Users by Login Count
          </h3>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {stats.topUsers.map((user, index) => (
              <div
                key={user.userId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  backgroundColor: isDarkMode ? '#333' : '#fff',
                  border: `1px solid ${isDarkMode ? '#444' : '#e9ecef'}`,
                  borderRadius: '6px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#0099FF',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}>
                    {index + 1}
                  </div>
                  <div>
                    <div style={{
                      color: isDarkMode ? 'white' : '#333',
                      fontWeight: '500'
                    }}>
                      {user.username}
                    </div>
                    <div style={{
                      fontSize: '0.85rem',
                      color: isDarkMode ? '#aaa' : '#666'
                    }}>
                      {user.email}
                    </div>
                  </div>
                </div>
                <div style={{
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  color: '#0099FF'
                }}>
                  {user.loginCount} {user.loginCount === 1 ? 'login' : 'logins'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Users by Connection Time */}
      {stats.topUsersByConnectionTime && stats.topUsersByConnectionTime.length > 0 && (
        <div
          style={{
            backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
            borderRadius: '8px',
            padding: '2rem',
            marginTop: '2rem'
          }}
        >
          <h3 style={{
            color: isDarkMode ? 'white' : '#333',
            marginBottom: '1.5rem',
            fontSize: '1.2rem'
          }}>
            Top Users by Connection Time
          </h3>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {stats.topUsersByConnectionTime.map((user, index) => (
              <div
                key={user.userId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  backgroundColor: isDarkMode ? '#333' : '#fff',
                  border: `1px solid ${isDarkMode ? '#444' : '#e9ecef'}`,
                  borderRadius: '6px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}>
                    {index + 1}
                  </div>
                  <div>
                    <div style={{
                      color: isDarkMode ? 'white' : '#333',
                      fontWeight: '500'
                    }}>
                      {user.username}
                    </div>
                    <div style={{
                      fontSize: '0.85rem',
                      color: isDarkMode ? '#aaa' : '#666'
                    }}>
                      {user.email}
                    </div>
                  </div>
                </div>
                <div style={{
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  color: '#6c757d'
                }}>
                  {formatDuration(user.totalConnectionTimeMinutes)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsDashboard;

